import { ViewIcon, EditIcon, PhoneIcon } from "@chakra-ui/icons";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  IconButton,
  Text,
  Image,
  Input,
  useToast,
  Box,
  Spinner,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import axios from "../../axiosConfig";
import socket from "../../socket"; // Ensure you have a shared socket instance
const ProfileModal = ({ user, children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState(null);
  const [profilePic, setProfilePic] = useState(user.pic);
  const [isUploading, setIsUploading] = useState(false);
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const storedUserInfo = localStorage.getItem("userInfo");
    if (storedUserInfo) {
      const userInfo = JSON.parse(storedUserInfo);
      setLoggedInUserId(userInfo._id);
    }
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setProfilePic(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast({
        title: "Invalid file type",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("userId", user._id);

    setIsUploading(true);
    try {
      const response = await axios.post("/api/google-drive/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updatedPicUrl = response.data?.googleDriveLink;
      if (updatedPicUrl) {
        setProfilePic(updatedPicUrl);
        const storedUserInfo = JSON.parse(localStorage.getItem("userInfo"));
        if (storedUserInfo?.token) {
          await fetchUserById(user._id, storedUserInfo.token);
          toast({
            title: "Image uploaded successfully",
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }
      } else {
        throw new Error("No valid image URL returned.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Failed to upload image",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const fetchUserById = async (userId, token) => {
    try {
      const response = await axios.get(`/api/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedUserInfo = JSON.parse(localStorage.getItem("userInfo"));
      if (updatedUserInfo) {
        updatedUserInfo.pic = response.data.pic;
        localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
        window.location.href = "/";
      }
    } catch (error) {
      console.error(
        "Error fetching user info:",
        error.response?.data || error.message
      );
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setProfilePic(user.pic);
    }
  }, [isOpen, user.pic]);

  return (
    <>
      {children ? (
        <span onClick={onOpen}>{children}</span>
      ) : (
        <Box display="flex" alignItems="center" gap={3}>
          {/* 📞 Call Button */}
          <IconButton
            icon={<PhoneIcon />}
            aria-label="Call User"
            colorScheme="green"
            onClick={() => {
              const caller = JSON.parse(localStorage.getItem("userInfo"));

              socket.emit("call-user", {
                to: user._id,
                from: caller._id,
                callerInfo: {
                  id: caller._id,
                  name: caller.name,
                  email: caller.email,
                  avatar: caller.pic,
                },
              });

              window.location.href = `/outgoing/${user._id}`;
            }}
          />

          {/* 👁️ View Profile Button */}
          <IconButton
            icon={<ViewIcon />}
            onClick={onOpen}
            aria-label="View Profile"
            ml={3}
          />
        </Box>
      )}

      <Modal size="lg" onClose={onClose} isOpen={isOpen} isCentered>
        <ModalOverlay />
        <ModalContent w={{ base: "90%", md: "500px" }}>
          <ModalHeader
            fontSize={{ base: "24px", md: "40px" }}
            textAlign="center"
          >
            {user.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDirection="column" alignItems="center">
            <Box position="relative" w="full" textAlign="center">
              <Image
                borderRadius="full"
                boxSize={{ base: "120px", md: "150px" }}
                src={profilePic}
                alt={user.name}
                mx="auto"
              />
              {loggedInUserId === user._id && (
                <>
                  <IconButton
                    icon={<EditIcon />}
                    position="absolute"
                    bottom="-10px"
                    right="10px"
                    size="sm"
                    onClick={() => document.getElementById("fileInput").click()}
                    aria-label="Edit Picture"
                  />
                  <Input
                    type="file"
                    id="fileInput"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <Button
                    mt={3}
                    onClick={handleFileUpload}
                    colorScheme="blue"
                    size="sm"
                    isLoading={isUploading}
                  >
                    {isUploading ? <Spinner size="sm" /> : "Upload"}
                  </Button>
                </>
              )}
            </Box>
            <Text fontSize={{ base: "16px", md: "18px" }} mt={3}>
              Email: {user.email}
            </Text>
            <Text
              fontSize={{ base: "12px", md: "16px" }}
              color="gray.500"
              mt={2}
            >
              User ID: {user._id}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} size="md" colorScheme="gray">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ProfileModal;
